// SPDX-FileCopyrightText: Copyright Orangebot, Inc.
// SPDX-License-Identifier: Apache-2.0

/**
 * VideoCallPiP Component
 *
 * A compact, draggable, resizable picture-in-picture window for active video calls.
 * Allows users to navigate the app while keeping the call visible.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Group,
  Text,
  ActionIcon,
  Badge,
  Tooltip,
  Paper,
  Portal,
} from '@mantine/core';
import {
  IconMicrophone,
  IconMicrophoneOff,
  IconVideo,
  IconVideoOff,
  IconPhoneOff,
  IconArrowsMaximize,
  IconGripVertical,
  IconChevronDown,
  IconScreenShare,
  IconScreenShareOff,
} from '@tabler/icons-react';
import { useCareComm } from '../../contexts/CareCommContext';
import { agoraService } from '../../services/agoraService';
import { AuthenticatedAvatar } from '../AuthenticatedAvatar';
import { getPatientPhotoUrl, getPatientInitials } from '../../utils/imageUtils';
import { formatHumanName } from '@medplum/core';
import classes from './VideoCallPiP.module.css';

// Container IDs for PiP video elements
const PIP_LOCAL_VIDEO_ID = 'pip-local-video';
const PIP_REMOTE_VIDEO_ID = 'pip-remote-video';

// Default position (bottom-right corner with padding)
const DEFAULT_POSITION = { x: window.innerWidth - 360, y: window.innerHeight - 320 };

// Default and min/max sizes
const DEFAULT_SIZE = { width: 320, height: 240 };
const MIN_SIZE = { width: 240, height: 180 };
const MAX_SIZE = { width: 640, height: 480 };

// LocalStorage keys for persisting position and size
const PIP_POSITION_KEY = 'carepoint-pip-position';
const PIP_SIZE_KEY = 'carepoint-pip-size';

export function VideoCallPiP() {
  const {
    activeCall,
    endCall,
    toggleMute,
    toggleCamera,
    toggleScreenShare,
    exitPiPMode,
    minimizeCall,
  } = useCareComm();

  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });

  // Load position from localStorage or use default
  const [position, setPosition] = useState(() => {
    try {
      const saved = localStorage.getItem(PIP_POSITION_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (
          typeof parsed.x === 'number' &&
          typeof parsed.y === 'number' &&
          parsed.x >= 0 &&
          parsed.y >= 0 &&
          parsed.x < window.innerWidth &&
          parsed.y < window.innerHeight
        ) {
          return parsed;
        }
      }
    } catch (e) {
      // Ignore parse errors
    }
    return DEFAULT_POSITION;
  });

  // Load size from localStorage or use default
  const [size, setSize] = useState(() => {
    try {
      const saved = localStorage.getItem(PIP_SIZE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (
          typeof parsed.width === 'number' &&
          typeof parsed.height === 'number' &&
          parsed.width >= MIN_SIZE.width &&
          parsed.height >= MIN_SIZE.height
        ) {
          return parsed;
        }
      }
    } catch (e) {
      // Ignore parse errors
    }
    return DEFAULT_SIZE;
  });

  // Handler for position changes
  const onPositionChange = useCallback((newPosition: { x: number; y: number }) => {
    setPosition(newPosition);
    try {
      localStorage.setItem(PIP_POSITION_KEY, JSON.stringify(newPosition));
    } catch (e) {
      // Ignore storage errors
    }
  }, []);

  // Handler for size changes
  const onSizeChange = useCallback((newSize: { width: number; height: number }) => {
    setSize(newSize);
    try {
      localStorage.setItem(PIP_SIZE_KEY, JSON.stringify(newSize));
    } catch (e) {
      // Ignore storage errors
    }
  }, []);

  // Handler for maximize (exit PiP mode back to fullscreen)
  const onMaximize = useCallback(() => {
    exitPiPMode();
  }, [exitPiPMode]);

  // Format call duration
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  // Handle drag start (mouse)
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    if (!containerRef.current) return;
    e.preventDefault();
    e.stopPropagation();

    const rect = containerRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setIsDragging(true);
  }, []);

  // Handle drag start (touch)
  const handleTouchDragStart = useCallback((e: React.TouchEvent) => {
    if (!containerRef.current) return;
    e.stopPropagation();

    const touch = e.touches[0];
    const rect = containerRef.current.getBoundingClientRect();
    setDragOffset({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    });
    setIsDragging(true);
  }, []);

  // Handle resize start (mouse)
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height,
    });
    setIsResizing(true);
  }, [size]);

  // Handle resize start (touch)
  const handleTouchResizeStart = useCallback((e: React.TouchEvent) => {
    e.stopPropagation();

    const touch = e.touches[0];
    setResizeStart({
      x: touch.clientX,
      y: touch.clientY,
      width: size.width,
      height: size.height,
    });
    setIsResizing(true);
  }, [size]);

  // Handle drag move (mouse and touch)
  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (clientX: number, clientY: number) => {
      const newX = clientX - dragOffset.x;
      const newY = clientY - dragOffset.y;

      // Keep within viewport bounds
      const maxX = window.innerWidth - size.width;
      const maxY = window.innerHeight - size.height;

      onPositionChange({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    };

    const handleMouseMove = (e: MouseEvent) => {
      handleMove(e.clientX, e.clientY);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        handleMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    };

    const handleEnd = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleEnd);
    window.addEventListener('touchcancel', handleEnd);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleEnd);
      window.removeEventListener('touchcancel', handleEnd);
    };
  }, [isDragging, dragOffset, size.width, size.height, onPositionChange]);

  // Handle resize move (mouse and touch)
  useEffect(() => {
    if (!isResizing) return;

    const handleResize = (clientX: number) => {
      const deltaX = clientX - resizeStart.x;

      // Maintain aspect ratio (4:3)
      const aspectRatio = 4 / 3;
      let newWidth = resizeStart.width + deltaX;
      let newHeight = newWidth / aspectRatio;

      // Clamp to min/max
      newWidth = Math.max(MIN_SIZE.width, Math.min(newWidth, MAX_SIZE.width));
      newHeight = Math.max(MIN_SIZE.height, Math.min(newHeight, MAX_SIZE.height));

      onSizeChange({
        width: Math.round(newWidth),
        height: Math.round(newHeight),
      });
    };

    const handleMouseMove = (e: MouseEvent) => {
      handleResize(e.clientX);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        handleResize(e.touches[0].clientX);
      }
    };

    const handleEnd = () => {
      setIsResizing(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleEnd);
    window.addEventListener('touchcancel', handleEnd);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleEnd);
      window.removeEventListener('touchcancel', handleEnd);
    };
  }, [isResizing, resizeStart, onSizeChange]);

  // Play remote video in PiP container when component mounts or displayMode changes to pip
  useEffect(() => {
    if (activeCall?.type === 'video-call' && activeCall.displayMode === 'pip') {
      // Small delay to ensure container is in DOM
      const timer = setTimeout(() => {
        try {
          agoraService.playRemoteVideo(PIP_REMOTE_VIDEO_ID);
          agoraService.playLocalVideo(PIP_LOCAL_VIDEO_ID);
        } catch (error) {
          console.warn('Failed to play video in PiP:', error);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [activeCall?.type, activeCall?.displayMode]);

  // Only render when in PiP mode
  if (!activeCall || activeCall.displayMode !== 'pip') return null;

  const patientName = activeCall.patient?.name?.[0]
    ? formatHumanName(activeCall.patient.name[0])
    : 'Patient';

  const isVideoCall = activeCall.type === 'video-call';
  const hasRemoteVideo = isVideoCall && !activeCall.isCameraOff;

  // Calculate video height based on container size (minus header and controls)
  const headerHeight = 36;
  const controlsHeight = 44;
  const videoHeight = size.height - headerHeight - controlsHeight;

  return (
    <Portal>
      <Paper
        ref={containerRef}
        className={classes.container}
        style={{
          left: position.x,
          top: position.y,
          width: size.width,
          cursor: isDragging ? 'grabbing' : 'default',
        }}
        shadow="xl"
        radius="md"
      >
        {/* Branded Header */}
        <Box
          className={classes.header}
          onMouseDown={handleDragStart}
          onTouchStart={handleTouchDragStart}
          style={{ cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
        >
          <Group justify="space-between" h="100%" px="xs" wrap="nowrap">
            <Group gap="xs" wrap="nowrap">
              <IconGripVertical size={14} className={classes.dragIcon} />
              <img
                src="/img/app-logo-wide.png"
                alt="CarePoint"
                className={classes.headerLogo}
              />
            </Group>
            <Group gap={4} wrap="nowrap">
              <Badge
                size="xs"
                color={activeCall.state === 'connected' ? 'green' : 'yellow'}
                variant="filled"
              >
                {activeCall.state === 'connected' ? formatDuration(activeCall.duration) : 'Connecting...'}
              </Badge>
              <Tooltip label="Back to full view" position="bottom">
                <ActionIcon
                  variant="subtle"
                  size="xs"
                  color="white"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMaximize();
                  }}
                >
                  <IconArrowsMaximize size={14} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>
        </Box>

        {/* Video Area */}
        <Box className={classes.videoArea} style={{ height: videoHeight }}>
          {isVideoCall && hasRemoteVideo ? (
            <>
              {/* Remote Video (Background) */}
              <Box
                id={PIP_REMOTE_VIDEO_ID}
                className={classes.remoteVideo}
              />
              {/* Local Video (Small overlay) */}
              {!activeCall.isCameraOff && (
                <Box
                  id={PIP_LOCAL_VIDEO_ID}
                  className={classes.localVideo}
                />
              )}
            </>
          ) : (
            /* Voice Call or Camera Off - Show Avatar */
            <Box className={classes.avatarContainer}>
              <AuthenticatedAvatar
                src={getPatientPhotoUrl(activeCall.patient)}
                size={64}
                radius="xl"
                color="blue"
              >
                {getPatientInitials(activeCall.patient)}
              </AuthenticatedAvatar>
              {/* Audio wave animation for voice calls */}
              {activeCall.state === 'connected' && (
                <Box className={classes.audioWave} />
              )}
            </Box>
          )}

          {/* Patient Name Overlay */}
          <Box className={classes.patientOverlay}>
            <Text size="xs" fw={600} c="white" lineClamp={1}>
              {patientName}
            </Text>
          </Box>
        </Box>

        {/* Controls Bar */}
        <Box className={classes.controls}>
          <Group gap="xs" justify="center">
            <Tooltip label={activeCall.isMuted ? 'Unmute' : 'Mute'} position="top">
              <ActionIcon
                variant={activeCall.isMuted ? 'filled' : 'subtle'}
                color={activeCall.isMuted ? 'red' : 'gray'}
                size="md"
                radius="xl"
                onClick={toggleMute}
              >
                {activeCall.isMuted ? (
                  <IconMicrophoneOff size={16} />
                ) : (
                  <IconMicrophone size={16} />
                )}
              </ActionIcon>
            </Tooltip>

            {isVideoCall && (
              <Tooltip label={activeCall.isCameraOff ? 'Camera on' : 'Camera off'} position="top">
                <ActionIcon
                  variant={activeCall.isCameraOff ? 'filled' : 'subtle'}
                  color={activeCall.isCameraOff ? 'red' : 'gray'}
                  size="md"
                  radius="xl"
                  onClick={toggleCamera}
                >
                  {activeCall.isCameraOff ? (
                    <IconVideoOff size={16} />
                  ) : (
                    <IconVideo size={16} />
                  )}
                </ActionIcon>
              </Tooltip>
            )}

            {isVideoCall && (
              <Tooltip label={activeCall.isScreenSharing ? 'Stop sharing' : 'Share screen'} position="top">
                <ActionIcon
                  variant={activeCall.isScreenSharing ? 'filled' : 'subtle'}
                  color={activeCall.isScreenSharing ? 'blue' : 'gray'}
                  size="md"
                  radius="xl"
                  onClick={toggleScreenShare}
                >
                  {activeCall.isScreenSharing ? (
                    <IconScreenShareOff size={16} />
                  ) : (
                    <IconScreenShare size={16} />
                  )}
                </ActionIcon>
              </Tooltip>
            )}

            <Tooltip label="Minimize to footer" position="top">
              <ActionIcon
                variant="subtle"
                color="gray"
                size="md"
                radius="xl"
                onClick={minimizeCall}
              >
                <IconChevronDown size={16} />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="End call" position="top">
              <ActionIcon
                variant="filled"
                color="red"
                size="md"
                radius="xl"
                onClick={endCall}
              >
                <IconPhoneOff size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Box>

        {/* Resize Handle */}
        <Box
          className={classes.resizeHandle}
          onMouseDown={handleResizeStart}
          onTouchStart={handleTouchResizeStart}
          style={{ touchAction: 'none' }}
        />
      </Paper>
    </Portal>
  );
}

export default VideoCallPiP;
